import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';

const allUsers = [
  { id: 'u1', name: 'Dr. Sarah Johnson', role: 'Safety Manager' },
  { id: 'u2', name: 'Michael Chen', role: 'Warehouse Manager' },
  { id: 'u3', name: 'Emily Rodriguez', role: 'EHS Officer' },
  { id: 'u4', name: 'Robert Thompson', role: 'Maintenance Lead' },
  { id: 'u5', name: 'Lisa Anderson', role: 'Process Engineer' },
  { id: 'u6', name: 'James Wilson', role: 'Operations Manager' },
  { id: 'u7', name: 'Maria Garcia', role: 'Quality Analyst' },
  { id: 'u8', name: 'David Brown', role: 'Site Coordinator' },
];

const priorityConfig = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  low: { label: 'Low', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
};

const statusConfig = {
  raised: { label: 'Raised', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  approved: { label: 'Approved', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  'in-progress': { label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  implemented: { label: 'Implemented', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  'effectiveness-check': { label: 'Effectiveness Check', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400' },
};

const emptyForm = {
  type: 'corrective',
  description: '',
  ownerId: '',
  dueDate: '',
  priority: 'medium',
  effectivenessCheckDue: '',
};

const RaiseRecommendationPanel = ({ recommendations, onAddRecommendation, onUpdateRecommendation, investigationApproved = false }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [expandedId, setExpandedId] = useState(null);

  const handleSubmit = () => {
    if (!form?.description || !form?.ownerId || !form?.dueDate) return;
    onAddRecommendation?.({
      ...form,
      id: Date.now(),
      status: 'raised',
      raisedAt: new Date()?.toISOString(),
    });
    setForm(emptyForm);
    setShowForm(false);
  };

  const getOwnerName = (id) => allUsers?.find(u => u?.id === id)?.name || '—';

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
            <Icon name="CheckSquare" size={18} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Recommendations</h3>
            <p className="text-xs text-muted-foreground">{recommendations?.length || 0} raised</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Icon name="Plus" size={13} />
          Raise Recommendation
        </button>
      </div>

      {showForm && (
        <div className="mb-5 p-4 bg-muted/40 border border-border rounded-xl space-y-4">
          <h4 className="text-sm font-semibold text-foreground">New Recommendation</h4>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Type *</label>
              <select
                value={form?.type}
                onChange={e => setForm(p => ({ ...p, type: e?.target?.value }))}
                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
              >
                <option value="corrective">Corrective</option>
                <option value="preventive">Preventive</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Priority *</label>
              <select
                value={form?.priority}
                onChange={e => setForm(p => ({ ...p, priority: e?.target?.value }))}
                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Description *</label>
            <textarea
              placeholder="Describe the corrective or preventive action required..."
              value={form?.description}
              onChange={e => setForm(p => ({ ...p, description: e?.target?.value }))}
              rows={3}
              className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Owner *</label>
              <select
                value={form?.ownerId}
                onChange={e => setForm(p => ({ ...p, ownerId: e?.target?.value }))}
                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
              >
                <option value="">Select owner...</option>
                {allUsers?.map(u => <option key={u?.id} value={u?.id}>{u?.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Due Date *</label>
              <input
                type="date"
                value={form?.dueDate}
                onChange={e => setForm(p => ({ ...p, dueDate: e?.target?.value }))}
                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Effectiveness Check Due</label>
              <input
                type="date"
                value={form?.effectivenessCheckDue}
                onChange={e => setForm(p => ({ ...p, effectivenessCheckDue: e?.target?.value }))}
                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={handleSubmit} className="px-4 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors">
              Raise Recommendation
            </button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); }} className="px-4 py-2 bg-muted text-muted-foreground text-xs font-medium rounded-lg hover:bg-muted/80 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {recommendations?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="CheckSquare" size={28} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No recommendations raised yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {recommendations?.map(rec => {
            const priority = priorityConfig?.[rec?.priority] || priorityConfig?.medium;
            const status = statusConfig?.[rec?.status] || statusConfig?.raised;
            const isExpanded = expandedId === rec?.id;
            return (
              <div key={rec?.id} className="border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : rec?.id)}
                  className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priority?.color}`}>{priority?.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${status?.color}`}>{status?.label}</span>
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs capitalize">{rec?.type}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground leading-snug">{rec?.description}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Icon name="User" size={11} />{getOwnerName(rec?.ownerId)}</span>
                      {rec?.dueDate && <span className="flex items-center gap-1"><Icon name="Calendar" size={11} />Due: {format(new Date(rec.dueDate), 'MMM dd, yyyy')}</span>}
                    </div>
                  </div>
                  <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border bg-muted/20">
                    <div className="pt-3 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Owner</p>
                        <p className="font-medium text-foreground">{getOwnerName(rec?.ownerId)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Due Date</p>
                        <p className="font-medium text-foreground">{rec?.dueDate ? format(new Date(rec.dueDate), 'MMM dd, yyyy') : '—'}</p>
                      </div>
                      {rec?.effectivenessCheckDue && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Effectiveness Check</p>
                          <p className="font-medium text-foreground">{format(new Date(rec.effectivenessCheckDue), 'MMM dd, yyyy')}</p>
                        </div>
                      )}
                      {rec?.raisedAt && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Raised</p>
                          <p className="font-medium text-foreground">{format(new Date(rec.raisedAt), 'MMM dd, yyyy')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RaiseRecommendationPanel;
